import React, {useEffect, useState} from 'react';
import {Button} from 'src/ui-components/Button/button';
import {CloseIcon, SaveIconBlue} from 'src/ui-components/Iconpack';
import {RootState, useAppDispatch} from 'src/store';
import {toast, ToastContainer} from 'react-toastify';
import {useSelector} from 'react-redux';
import {getFileDataFromUrl, getFileMetaData, saveImageMediaFile} from 'src/actions/media';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {FileData} from 'src/reducers/media';
import Tracker from 'src/lib/analytics/tracker';
import {EventNames, EventProperty, ScreenNames} from 'src/lib/analytics/events';
import {getSettings} from '../../actions/settings';
import SideMenu from '../../components/side-menu/SideMenu';
import {getImageSize} from '../../utils/media';
import {isSvgOrGif, PaddingColor, TransformValues} from '../../utils/helper';
import ImageUploading from '../../components/image-uploading/ImageUploading';
import {Dialog, DialogContent} from '../../ui-components/Modal';
import TransformationComponent from '../../components/tranformation-component/TransformationComponent';
import {getImageWidthAndHeight} from 'src/utils/media';
import {TransformationType} from './Transformation';
import {sendMessage, TransformationSavePayload} from '../../messaging';
import {DEFAULT_IMG_HOST} from '../../constants';

export interface CropProps {
    cropLeftPx: number;
    cropTopPx: number;
    cropHeightPx: number;
    cropWidthPx: number;
}

export default function TransformationV2() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const [fileData, setFileData] = useState<FileData>();
    const [prevTransform, setTransform] = useState<any>();
    const queryParams = new URLSearchParams(location.search);

    const {fileUid} = useParams();

    const [mediaProps, userProfileProps, settingsProps] = [
        useSelector((state: RootState) => state.media),
        useSelector((state: RootState) => state.userProfile),
        useSelector((state: RootState) => state.settings),
    ];
    let cdnUrl = '',
        modifiedCdnUrl = '',
        fileName = '';

    const svgOrGif = isSvgOrGif(fileData?.name);

    const [embeddedUrl, setEmbeddedUrl] = useState<string | null | undefined>();
    const [transformId, setTransformId] = useState<string | null | undefined>();
    const [width, setWidth] = useState('');
    const [height, setHeight] = useState('');
    const [blur, setBlur] = useState<number>();
    const [cropProps, setCropProps] = useState<CropProps>();
    const [enablePadding, setPadding] = useState(false);
    const [paddingColor, setPaddingColor] = useState<PaddingColor>();
    const [isGreyToneApplied, setGreyToneApplied] = useState(false);
    const [isBgRemoved, setBgRemoved] = useState(false);
    const [imageFormat, setImageFormat] = useState('');
    const [quality, setQuality] = useState<string>('');
    const [modifiedImageFileSize, setModifiedImageFileSize] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [imageSrc, setImageSrc] = useState('');
    const [showImageSaving, setShowImageSaving] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [upgradeReq, setUpgradeRequired] = useState(false);
    const [transformedUid, setTransformedUid] = useState('');
    const [errorOccurred, setErrorOccurred] = useState(false);
    const [bgColorPalette, setBgColorPalette] = useState('');
    const [backgroundImageUid, setBackgroundImageUid] = useState('');
    const [aspectRatio, setAspectRatio] = useState(0);
    const [transformsStack, setTransformsTack] = useState<TransformationType[]>([]);
    const [isEmbedded, setIsEmbedded] = useState(false);
    const [rotation, setRotation] = useState(0);

    cdnUrl = mediaProps.fileData.cdn_url;
    fileName = mediaProps.fileData.name;

    let transformationLiterals: string[] = [];
    const transformationObject: TransformValues = {};

    transformationObject.upgradeRequired = upgradeReq;
    transformationObject.fileName = fileName;
    transformationObject.isSvgOrGif = svgOrGif;
    transformationObject.orgUid = userProfileProps.currentOrganization.uid;

    if (rotation && rotation > 0) {
        transformationLiterals.push('a_' + rotation);
        transformationObject.rotation = rotation;
    }

    if (blur) {
        transformationLiterals.push(`blur_${blur}_0.5`);
        transformationObject.blur = blur;
    }

    if (width.length > 0 && parseInt(width.trim()) > 0) {
        transformationLiterals.push('w_' + width);
        transformationObject.width = width;
    }

    if (bgColorPalette) {
        transformationLiterals.push('i_bgreplace,b_' + bgColorPalette.substring(1));
    }

    if (backgroundImageUid) {
        transformationLiterals.push('i_bgreplace,b_uid_' + backgroundImageUid);
        transformationObject.backgroundImageUid = backgroundImageUid;
    }

    if (height.length > 0 && parseInt(height.trim()) > 0) {
        transformationLiterals.push('h_' + height);
        transformationObject.height = height;
    }

    if (paddingColor?.padRgb && paddingColor?.padRgb.length > 0) {
        transformationLiterals.push('b_' + paddingColor?.padRgb);
        transformationObject.paddingColor = {padRgb: paddingColor?.padRgb, padHex: paddingColor?.padHex};
    }

    if (enablePadding) {
        transformationLiterals.push('c_pad');
        transformationObject.padding = true;
    }

    if (imageFormat.length > 0) {
        transformationLiterals.push('f_' + imageFormat);
        transformationObject.format = imageFormat;
    }

    if (quality.length > 0) {
        transformationLiterals.push('q_auto:' + quality);
        transformationObject.quality = quality;
    }

    if (isGreyToneApplied) {
        transformationLiterals.push('e_grayscale');
        transformationObject.isGreyToneApplied = true;
    }

    if (isBgRemoved) {
        transformationLiterals.push('e_bgremoval');
        transformationObject.isBgRemoved = true;
    }

    if (cropProps) {
        transformationLiterals.push('c_crop');
        transformationLiterals.push('c_top_' + cropProps.cropTopPx);
        transformationLiterals.push('c_left_' + cropProps.cropLeftPx);
        transformationLiterals.push('c_w_' + cropProps.cropWidthPx);
        transformationLiterals.push('c_h_' + cropProps.cropHeightPx);
        transformationObject.cropProps = cropProps;
    }

    if (aspectRatio) {
        transformationObject.ar = aspectRatio;
    }

    if (transformationLiterals.length > 0) {
        let foundRotation = null;
        const filteredArray = transformationLiterals.filter((element) => {
            if (/^a_/.test(element)) {
                foundRotation = element;
                return false;
            }
            return true;
        });
        let modifiedUrlParts = cdnUrl.split('/');
        let prefixCount = 4;
        if (DEFAULT_IMG_HOST && !cdnUrl.startsWith(DEFAULT_IMG_HOST)) {
            prefixCount = 3;
        }
        const hostPart = modifiedUrlParts.slice(0, prefixCount);
        const filePath = modifiedUrlParts.slice(prefixCount);
        if (foundRotation) {
            if (filteredArray.length) {
                modifiedCdnUrl =
                    hostPart.join('/') + '/' + filteredArray.join(',') + '/' + foundRotation + '/' + filePath.join('/');
            } else {
                modifiedCdnUrl = hostPart.join('/') + '/' + foundRotation + '/' + filePath.join('/');
            }
        } else {
            modifiedCdnUrl = hostPart.join('/') + '/' + filteredArray.join(',') + '/' + filePath.join('/');
        }
    }

    if (modifiedCdnUrl.length > 0) {
        getImageSize(modifiedCdnUrl).then((size) => {
            setModifiedImageFileSize(size);
        });
    }

    const rotateImage = (angle: number) => {
        setRotation(angle);
    };

    const addTransformation = (t: TransformationType) => {
        setTransformsTack((prevTransforms) => {
            const filteredTransforms = prevTransforms.filter((transform) => transform !== t);
            return [...filteredTransforms, t];
        });
    };

    const removeTransformation = (t: TransformationType) => {
        setTransformsTack((prevTransforms) => {
            return prevTransforms.filter((transform) => transform !== t);
        });
    };

    const undoLastTransformation = () => {
        if (!transformsStack.length) {
            return;
        }

        const lastTransformation = transformsStack.pop();
        setTransformsTack((prevTransforms) => {
            return prevTransforms.slice(0, prevTransforms.length - 1);
        });

        if (lastTransformation) {
            switch (lastTransformation) {
                case TransformationType.HEIGHT:
                case TransformationType.WIDTH:
                    setHeight('');
                    setWidth('');
                    break;
                case TransformationType.PADDING:
                    setPadding(false);
                    setPaddingColor(undefined);
                    break;
                case TransformationType.BG_REMOVE:
                case TransformationType.BG_CLR:
                case TransformationType.BG_IMG:
                    setBgRemoved(false);
                    setBgColorPalette('');
                    setBackgroundImageUid('');
                    break;
                case TransformationType.CROP:
                    setCropProps(undefined);
                    break;
                case TransformationType.QUALITY:
                    setQuality('');
                    break;
                case TransformationType.FORMAT:
                    setImageFormat('');
                    break;
                case TransformationType.GRAY_EFFECT:
                    setGreyToneApplied(false);
                    break;
                default:
                    break;
            }
        }
    };

    const handleCloseButton = () => {
        Tracker.trackEvent(EventNames.TRANSFORMATION_NOT_SAVED);
        navigate(-1);
    };

    const saveImage = async () => {
        if (!isEmbedded) {
            if (modifiedCdnUrl !== '') {
                if (prevTransform && prevTransform.url === modifiedCdnUrl) {
                    toast.warning('No changes made to the version yet');
                    return;
                }
                const body = {
                    orgUid: userProfileProps.currentOrganization.uid,
                    mediaFileUid: fileUid ? fileUid : '',
                    imageUrl: modifiedCdnUrl,
                };
                Tracker.trackEvent(EventNames.TRANSFORMATION_SAVE_CLICKED);
                setIsSaving(true);
                setShowImageSaving(true);
                await dispatch(saveImageMediaFile(body));
            } else {
                toast.warning('No changes made to the file');
            }
        } else {
            const payload: TransformationSavePayload = {
                transform: transformationLiterals.join(','),
                cdnUrl: modifiedCdnUrl,
            };
            sendMessage('PerceptPixel.Transformation.Save', payload, window.parent);
        }
    };
    const suspendSaveDialog = () => {
        setShowImageSaving(false);
        setIsSaving(false);
        setIsSaved(false);
        setErrorOccurred(false);
    };

    const calculateCropValue = (dimension: number, percent: number) => {
        return percent === 0 ? 0 : Math.round((dimension * percent) / 100);
    };

    const applyCrop = async (xPercent: number, yPercent: number, wPercent: number, hPercent: number) => {
        if (!fileData) return;
        let w: number;
        let h: number;

        if (height || width) {
            try {
                const [modifiedW, modifiedH] = await getImageWidthAndHeight(modifiedCdnUrl);
                w = modifiedW;
                h = modifiedH;
            } catch (e) {
                console.error('Error fetching image dimensions', modifiedCdnUrl, e);
                return;
            }
        } else {
            w = fileData.width;
            h = fileData.height;
        }

        if (w > 0 && h > 0) {
            addTransformation(TransformationType.CROP);
            setCropProps({
                cropLeftPx: calculateCropValue(w, xPercent),
                cropTopPx: calculateCropValue(h, yPercent),
                cropHeightPx: calculateCropValue(h, hPercent),
                cropWidthPx: calculateCropValue(w, wPercent),
            });
        }
    };

    const onTransformValuesChange = (values: TransformValues) => {
        if (values.padding !== undefined && values.padding !== null) {
            setPadding(values.padding);
        }
        if (values.paddingColor !== undefined && values.paddingColor !== null) {
            setPaddingColor(values.paddingColor);
        }
        if (values.format) {
            setImageFormat(values.format);
        }
        if (values.quality) {
            setQuality(values.quality);
        }
        if (values.width !== undefined && values.width !== null) {
            setWidth(values.width);
        }
        if (values.height !== undefined && values.height !== null) {
            setHeight(values.height);
        }
        if (values.isBgRemoved !== undefined) {
            setBgRemoved(values.isBgRemoved);
        }
        if (values.isGreyToneApplied !== undefined) {
            setGreyToneApplied(values.isGreyToneApplied);
        }
        if (values.bgColorPalette) {
            setBackgroundImageUid('');
            setBgRemoved(false);
            setBgColorPalette(values.bgColorPalette);
        }
        if (values.backgroundImageUid) {
            setBackgroundImageUid(values.backgroundImageUid);
            setBgRemoved(false);
            setBgColorPalette('');
        }
        if (values.ar) {
            setAspectRatio(values.ar);
        }
        if (values.blur !== undefined && values.blur !== null) {
            setBlur(values.blur);
        }
        if (values.upgradeRequired === undefined || values.upgradeRequired === null) {
            setUpgradeRequired(false);
        } else {
            setUpgradeRequired(values.upgradeRequired);
        }
    };

    const backgroundRemoveActions = () => {
        if (!isBgRemoved) {
            addTransformation(TransformationType.BG_REMOVE);
        } else {
            removeTransformation(TransformationType.BG_REMOVE);
        }
        setBgRemoved(!isBgRemoved);
        setBackgroundImageUid('');
        setBgColorPalette('');
    };

    function applyExistingTransformations(transformation_url: string) {
        const parts = transformation_url.split(',');

        parts.forEach((part) => {
            if (part.match(/^h_(\d+)$/)) {
                setHeight(part.replace('h_', ''));
            } else if (part.match(/^w_(\d+)$/)) {
                setWidth(part.replace('w_', ''));
            } else if (part.match(/^f_(.+)$/)) {
                setImageFormat(part.replace('f_', ''));
            } else if (part.match(/^q_auto:(.+)$/)) {
                setQuality(part.replace('q_auto:', ''));
            } else if (part === 'e_bgremoval') {
                setBgRemoved(true);
            } else if (part === 'e_grayscale') {
                setGreyToneApplied(true);
            } else if (part === 'c_pad') {
                setPadding(true);
            } else if (part.match(/^b_\d+-\d+-\d+$/)) {
                setPaddingColor({padRgb: part.replace('b_', '')});
            } else if (part.match(/^b_uid_(.+)$/)) {
                setBackgroundImageUid(part.replace('b_uid_', ''));
            } else if (part.match(/^b_(.+)$/)) {
                setBgColorPalette(part.replace('b_', ''));
            } else {
                console.error('Invalid transformation prop inside transform', transformId, part);
            }
        });

        // crop props
        if (parts.some((part) => part.startsWith('c_crop'))) {
            const cropProps = parts.reduce(
                (acc, part) => {
                    if (part.startsWith('c_left_')) {
                        acc.cropLeftPx = parseInt(part.replace('c_left_', ''), 10);
                    } else if (part.startsWith('c_top_')) {
                        acc.cropTopPx = parseInt(part.replace('c_top_', ''), 10);
                    } else if (part.startsWith('c_w_')) {
                        acc.cropWidthPx = parseInt(part.replace('c_w_', ''), 10);
                    } else if (part.startsWith('c_h_')) {
                        acc.cropHeightPx = parseInt(part.replace('c_h_', ''), 10);
                    }
                    return acc;
                },
                {
                    cropLeftPx: 0,
                    cropTopPx: 0,
                    cropWidthPx: 0,
                    cropHeightPx: 0,
                },
            );

            setCropProps(cropProps);
        }
    }

    const onCropCancel = () => {
        setAspectRatio(0);
    };

    useEffect(() => {
        setTransformId(queryParams.get('transformId'));
        if (queryParams.get('url')) {
            setIsEmbedded(true);
            setEmbeddedUrl(queryParams.get('url'));
        }
        Tracker.setCurrentScreenName(ScreenNames.TRANSFORMATION_SCREEN);
        Tracker.trackEvent(EventNames.SCREEN_VIEWS, {
            [EventProperty.ScreenName]: ScreenNames.TRANSFORMATION_SCREEN,
        });
    }, []);

    useEffect(() => {
        if (userProfileProps.currentOrganization.uid.length > 0 && fileUid !== 'embed') {
            dispatch(getFileMetaData({orgUid: userProfileProps.currentOrganization.uid, fileUid: fileUid!!}));
        } else {
            if (fileUid === 'embed' && embeddedUrl) {
                dispatch(getFileDataFromUrl({fileUrl: embeddedUrl})).then(() => {
                    const transform = queryParams.get('transform');
                    if (transform) {
                        applyExistingTransformations(transform);
                    }
                });
            }
        }
    }, [userProfileProps.currentOrganization, embeddedUrl]);

    useEffect(() => {
        if (mediaProps.fileData.cdn_url !== '') {
            setFileData(mediaProps.fileData);
            if (transformId && transformId.length > 0 && mediaProps.fileData.saved_images[transformId]) {
                setTransform(mediaProps.fileData.saved_images[transformId]);
            }
        }
    }, [mediaProps]);

    useEffect(() => {
        if (mediaProps.savedImageFiles) {
            setIsSaving(false);
            setIsSaved(true);
            setTransformedUid(mediaProps.savedImageFiles.media_image_uid);
        } else if (mediaProps.errorSavingImageFile) {
            setIsSaving(false);
            setIsSaved(false);
            setErrorOccurred(true);
            toast.error('Something went wrong');
        }
    }, [mediaProps.savedImageFiles, mediaProps.errorSavingImageFile]);

    useEffect(() => {
        const transformUrl = prevTransform?.url ?? '';
        const match = transformUrl.match(/^https?:\/\/([^/]+)\/([^/]+)\/([^/]+)\/([^/]+)/);
        if (match && match.length >= 3) {
            const props = match[3];
            applyExistingTransformations(props);
        }
    }, [prevTransform]);

    useEffect(() => {
        const org_uid = userProfileProps.currentOrganization.uid;
        if (org_uid) {
            dispatch(getSettings(org_uid));
        }
    }, [userProfileProps.currentOrganization]);

    useEffect(() => {
        setIsLoading(true);

        const imageUrl = modifiedCdnUrl.length > 0 ? modifiedCdnUrl : cdnUrl;
        const img = new Image();

        img.onload = () => {
            setImageSrc(imageUrl);
            setIsLoading(false);
        };

        img.onerror = (event) => {
            undoLastTransformation();

            let msg = 'Oops! Something went wrong. Please try again';
            if (modifiedCdnUrl.length) {
                toast.error(msg, {autoClose: 5000});
            }

            Tracker.trackEvent(
                EventNames.TRANSFORMATION_FAILURE,
                {
                    [EventProperty.FileUid]: fileUid,
                },
                {
                    transformId: transformId,
                    tStack: JSON.stringify(transformsStack),
                    cdnUrl: cdnUrl,
                    modifiedCdnUrl: modifiedCdnUrl,
                },
            );
            setIsLoading(false);
        };

        img.src = imageUrl;

        return () => {
            img.onload = img.onerror = null; // Remove event listeners on unmount
        };
    }, [modifiedCdnUrl, cdnUrl]);

    return (
        <>
            {/*<LiveChatWidget license="16937379" visibility='hidden'/>*/}
            <div className='flex w-full justify-end py-2 md:hidden'>
                <Button
                    variant='nude'
                    size='sm'
                    className='text-[14px] font-[600] text-[#155EEF]'
                    icon
                    onClick={saveImage}>
                    <SaveIconBlue />
                    Save Image
                </Button>
                {!isEmbedded && (
                    <Button variant='nude' size='sm' icon onClick={handleCloseButton}>
                        <CloseIcon />
                    </Button>
                )}
            </div>
            <div className='flex h-[100vh] bg-[#101323]'>
                <SideMenu
                    isEmbedded={isEmbedded}
                    onTransformValuesChange={onTransformValuesChange}
                    transformationObject={transformationObject}
                    addTransformation={addTransformation}
                    removeTransformation={removeTransformation}
                    cdnUrl={cdnUrl}
                />
                <TransformationComponent
                    isEmbedded={isEmbedded}
                    aspectRatio={aspectRatio}
                    allowBgRemoval={settingsProps.allow_background_removal}
                    backgroundRemoveActions={backgroundRemoveActions}
                    cdnUrl={cdnUrl}
                    handleCloseButton={handleCloseButton}
                    isBgRemoved={isBgRemoved}
                    isLoading={isLoading || settingsProps.isLoading}
                    modifiedCdnUrl={modifiedCdnUrl}
                    saveImage={saveImage}
                    svgOrGif={svgOrGif}
                    onCropConfirm={applyCrop}
                    onCropCancel={onCropCancel}
                    onRotate={rotateImage}
                    rotation={rotation}
                    changeLogProps={{
                        bgRemoved: isBgRemoved,
                        bgReplaced: bgColorPalette.length > 0 || backgroundImageUid.length > 0,
                        cropped: cropProps !== undefined,
                        dimensionsChanged: height.length > 0 || width.length > 0,
                        greyToneApplied: isGreyToneApplied,
                        loading: isLoading,
                        originalFileType: fileData?.type ?? '',
                        newFileType: imageFormat,
                        originalFileSize: fileData?.file_size ?? 0,
                        cdnUrl: modifiedCdnUrl.length > 0 ? modifiedCdnUrl : cdnUrl,
                        fileQuality: quality,
                    }}
                />
            </div>
            <Dialog open={showImageSaving}>
                <DialogContent
                    className='flex w-[333px] px-[40px] py-[22px] md:w-[1160px] md:px-10 md:py-10'
                    onClose={suspendSaveDialog}>
                    <ImageUploading
                        imageData={transformationObject}
                        modifiedImageFileSize={modifiedImageFileSize}
                        originalFileSize={fileData?.file_size}
                        isSaving={isSaving}
                        isSaved={isSaved}
                        imageUrl={modifiedCdnUrl}
                        fileName={mediaProps.fileData.name}
                        orgUid={userProfileProps.currentOrganization.uid}
                        fileUid={mediaProps.fileData.uid}
                        transformedUid={transformedUid}
                        errorOccurred={errorOccurred}
                        onClose={suspendSaveDialog}
                        retrySave={saveImage}
                    />
                </DialogContent>
            </Dialog>
            <ToastContainer
                className={'mx-[10px] mt-[50px] text-[14px]'}
                position={'top-right'}
                closeOnClick={true}
                pauseOnHover={false}
                autoClose={3000}
            />
        </>
    );
}
